// SSE client manager using native EventSource
// - Supports multiple listeners per event type
// - Configurable delays for connection establishment and cleanup
// - Auto-cleanup with configurable delay when no subscribers remain

import * as Sentry from "@sentry/react";
import { isEqual } from "lodash";

type Listener<T> = (data: T) => void;

type Status = "active" | "inactive" | "pending";

interface SSEConfig {
  connectionName: string;
  endpoint: string;
  retryEnabled?: boolean;
  cleanupDelayMs?: number; // Delay before closing connection when no subscribers remain
  connectionDelayMs?: number; // Delay before establishing the SSE connection
}

interface ConnectionParams {
  urlParams?: Record<string, string>;
}

export class SSEClientManager {
  private sse: EventSource | null = null;
  private status: Status = "inactive";
  private listeners: Record<string, Listener<unknown>[]> = {};
  private config: SSEConfig;
  private currentConnection: ConnectionParams | null = null;

  private retryCount = 0;
  private retryTimerRef: number | undefined;
  private cleanupTimerRef: number | undefined;
  private connectionTimerRef: number | undefined;

  constructor(config: SSEConfig) {
    this.config = config;
  }

  start(params: ConnectionParams) {
    // if the urlParams have changed, restart the connection
    if (!isEqual(this.currentConnection?.urlParams, params.urlParams)) {
      this.cleanup();
    }
    // If already connecting or connected, do nothing
    else if (this.status == "pending" || this.status == "active") {
      return;
    }

    this.currentConnection = params;
    this.cleanup();
    this.setStatus("pending");

    const connectionDelayMs = this.config.connectionDelayMs ?? 0;

    if (connectionDelayMs > 0) {
      console.log(
        `Delaying ${this.config.connectionName} SSE connection by ${connectionDelayMs}ms...`
      );
      this.connectionTimerRef = setTimeout(() => {
        this.establishConnection(params);
      }, connectionDelayMs);
    } else {
      this.establishConnection(params);
    }
  }

  private establishConnection(params: ConnectionParams) {
    let endpoint;

    try {
      endpoint = new URL(this.config.endpoint);
    } catch {
      // If the endpoint is a relative URL, construct it using the current origin
      endpoint = new URL(this.config.endpoint, window.location.origin);
    }

    if (params.urlParams) {
      Object.entries(params.urlParams).forEach(([key, value]) => {
        endpoint.searchParams.append(key, value);
      });
    }

    this.sse = new EventSource(endpoint.toString());

    this.sse.onmessage = (event: MessageEvent) => {
      if (!event.data) return;

      const parsed = this.safeParse(event.data);

      (this.listeners[event.type] || []).forEach((fn) => {
        if (!parsed) {
          console.error("Failed to parse SSE message", event.data);
          return;
        }
        try {
          fn(parsed);
        } catch (error) {
          console.error("Error in message callback:", error);
          console.error("Message data:", parsed);
          Sentry.captureException(error, {
            extra: {
              messageData: parsed,
              connectionName: this.config.connectionName,
            },
          });
        }
      });
    };

    this.sse.onopen = () => {
      console.log(`Connected to ${this.config.connectionName} SSE`);

      this.setStatus("active");
      this.retryCount = 0;
    };

    // on error, close to suppress native auto-reconnect, then retry with custom backoff
    this.sse.onerror = () => {
      console.error(`${this.config.connectionName} SSE connection error`);

      this.setStatus("inactive");
      this.cleanup();
      if (this.config.retryEnabled !== false) {
        this.reconnectWithDelay();
      }
    };
  }

  /**
   * Update stored URL params without reconnecting.
   * Ensures the next reconnect (e.g. after an error) uses the latest params.
   */
  updateUrlParams(urlParams: Record<string, string>) {
    if (this.currentConnection) {
      this.currentConnection = {
        ...this.currentConnection,
        urlParams,
      };
    }
  }

  subscribe<T>(type: "message" | "onStatusChange", callback: Listener<T>) {
    // Clear any pending cleanup since we have a new subscriber
    clearTimeout(this.cleanupTimerRef);
    this.cleanupTimerRef = undefined;

    this.listeners[type] = this.listeners[type] || [];
    this.listeners[type].push(callback as Listener<unknown>);

    if (type === "onStatusChange") {
      setTimeout(() => callback(this.status as T), 0);
    }

    return () => this.unsubscribe(type, callback);
  }

  unsubscribe<T>(type: "message" | "onStatusChange", callback: Listener<T>) {
    this.listeners[type] = (this.listeners[type] || []).filter(
      (fn) => fn !== callback
    );

    // Prune empty listener arrays to prevent unbounded growth
    if (this.listeners[type]?.length === 0) {
      delete this.listeners[type];
    }

    // Auto-close if no subscribers remain after configurable delay
    // Don't close immediately to allow component remounts on dashboard tab switching
    // When switching tabs, components unmount then remount quickly, in the process calling unsubscribe then subscribe
    const cleanupDelayMs = this.config.cleanupDelayMs ?? 3000;

    // Clear any existing cleanup timer
    clearTimeout(this.cleanupTimerRef);

    this.cleanupTimerRef = setTimeout(() => {
      if (this.hasNoSubscribers()) {
        this.cleanup();
      }
    }, cleanupDelayMs);
  }

  private reconnectWithDelay() {
    if (!this.currentConnection) {
      console.error("Cannot reconnect: no current connection stored");
      return;
    }

    const delayInMs = (this.retryCount + 1) * 3000;

    console.log(
      `Reconnecting to ${this.config.connectionName} SSE in ${delayInMs / 1000} seconds...`
    );

    this.retryTimerRef = setTimeout(
      () => this.start(this.currentConnection!),
      delayInMs
    );

    this.retryCount++;
  }

  private setStatus(newStatus: Status) {
    if (this.status !== newStatus) {
      this.status = newStatus;

      (this.listeners["onStatusChange"] ?? []).forEach((fn) => {
        try {
          fn(newStatus);
        } catch {
          Sentry.captureException(new Error("Error in status change callback"));
        }
      });
    }
  }

  private hasNoSubscribers() {
    return Object.keys(this.listeners).every(
      (key) => this.listeners[key].length === 0
    );
  }

  private cleanup() {
    clearTimeout(this.retryTimerRef);
    clearTimeout(this.cleanupTimerRef);
    clearTimeout(this.connectionTimerRef);

    if (this.sse) {
      this.sse.close();
      this.sse = null;
    }

    this.retryTimerRef = undefined;
    this.cleanupTimerRef = undefined;
    this.connectionTimerRef = undefined;
  }

  private safeParse<T = unknown>(data: string): T | null {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
}
